import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { generateICPAccounts } from '../../api/onboarding';
import type { ICPAccount } from '../../api/onboarding';

export interface OnboardingIcpValidatorProps {
  icpText: string;
  /** When true, validation requests are not started (e.g. while submitting the form). */
  disabled?: boolean;
  /** Called when validity for the current text changes (false while debouncing or if invalid). */
  onValidityChange: (isValid: boolean) => void;
  /** Latest successful generate result for this ICP text (used to avoid a second generate on submit). */
  onValidatedAccounts: (accounts: ICPAccount[], icpTrimmed: string) => void;
}

type DisplayState = 'idle' | 'checking' | 'valid' | 'invalid';

const DEBOUNCE_MS = 650;

export const OnboardingIcpValidator: React.FC<OnboardingIcpValidatorProps> = ({
  icpText,
  disabled = false,
  onValidityChange,
  onValidatedAccounts
}) => {
  const [display, setDisplay] = useState<DisplayState>('idle');
  const [invalidMessage, setInvalidMessage] = useState<string | undefined>();
  const [accountCount, setAccountCount] = useState<number | undefined>();

  useEffect(() => {
    if (disabled) {
      return;
    }

    const trimmed = icpText.trim();
    if (!trimmed) {
      setDisplay('idle');
      setInvalidMessage(undefined);
      setAccountCount(undefined);
      onValidityChange(false);
      return;
    }

    setDisplay('idle');
    setInvalidMessage(undefined);
    setAccountCount(undefined);
    onValidityChange(false);

    const ac = new AbortController();
    const timer = window.setTimeout(() => {
      void (async () => {
        setDisplay('checking');
        try {
          const accounts = await generateICPAccounts(trimmed, ac.signal);
          if (ac.signal.aborted) return;
          onValidatedAccounts(accounts, trimmed);
          setDisplay('valid');
          setAccountCount(accounts.length);
          onValidityChange(true);
        } catch (e: unknown) {
          if (ac.signal.aborted) return;
          const isAbort = e instanceof Error && e.name === 'AbortError';
          if (isAbort) return;
          setDisplay('invalid');
          const message =
            e instanceof Error ? e.message : 'Something went wrong. Try again.';
          setInvalidMessage(message);
          onValidityChange(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [icpText, disabled, onValidityChange, onValidatedAccounts]);

  return (
    <Box sx={{ mt: 0.5, mb: 1.5 }} aria-live="polite">
      {display === 'idle' && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          <Box sx={{ width: 18, height: 18, flexShrink: 0 }} aria-hidden />
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: 'transparent',
              userSelect: 'none',
              caretColor: 'transparent'
            }}
            aria-hidden
          >
            {/* Use an empty non-breaking space to keep the box's height consistent when empty. */}
            {'\u00A0'}
          </Typography>
        </Box>
      )}
      {display === 'checking' && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking your ICP…
          </Typography>
        </Box>
      )}
      {display === 'valid' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%'
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 18,
              color: 'success.main',
              flexShrink: 0,
              mt: 0.125
            }}
            aria-hidden
          />
          <Typography variant="body2" color="success.main" component="span">
            {`Found ${accountCount} matching ${accountCount === 1 ? 'company' : 'companies'}.`}
          </Typography>
        </Box>
      )}
      {display === 'invalid' && invalidMessage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%'
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: 18,
              color: 'error.main',
              flexShrink: 0,
              mt: 0.125
            }}
            aria-hidden
          />
          <Typography variant="body2" color="error" component="span">
            {invalidMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OnboardingIcpValidator;
